/* ohm-klaviyo-bis.js — send back-in-stock requests to Klaviyo as well as to the
   contact inbox.

   WHY THIS EXISTS (#161)

   Concept's back-in-stock control is a Shopify `{% form 'contact' %}`. It emails
   support@ with the product title in the message body and nothing else. It
   creates no subscription record, so `Subscribed to Back in Stock` in Klaviyo
   read 0 for twelve straight months — and, more expensively, nobody was ever
   notified automatically when stock returned. That only happened if a human
   read those emails and acted, which is how the June Discover restock was run.

   This adds the missing half. The contact email still sends: if the Klaviyo
   call fails the request still reaches a person, which is the safe behaviour
   during rollout. Once Klaviyo's Back in Stock flow is live and proven, the
   contact form can be retired — see #161.

   HOW IT WORKS

   On submit we hold the native submission, POST the subscription to Klaviyo's
   client endpoint, then let the form continue. `sendBeacon` is not usable here
   because Klaviyo requires a `revision` header and beacons cannot set headers,
   so the POST has to complete (or time out) before navigation.

   The company ID is read from the Klaviyo onsite script already on the page
   rather than hardcoded — it is a public key, but one copy is better than two.
*/

(function () {
  'use strict';

  // The snippet renders more than once per PDP, so this file can be included
  // twice. Wiring is guarded per-form below, but the MutationObserver is not —
  // without this, each include would add another observer over the whole tree.
  if (window.__ohmKlaviyoBisInit) return;
  window.__ohmKlaviyoBisInit = true;

  var KLAVIYO_REVISION = '2024-10-15'; // pinned; bump deliberately
  var TIMEOUT_MS = 2500;               // never hold the user longer than this

  function companyId() {
    var el = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
    if (!el) return null;
    var m = el.src.match(/[?&]company_id=([^&]+)/);
    if (m) return m[1];
    m = el.src.match(/\/onsite\/js\/([^/.]+)\//);
    return m ? m[1] : null;
  }

  // The variant the shopper actually has selected, not the one the page loaded
  // with. Concept keeps it as JSON in [data-selected-variant] and rewrites it on
  // every option change; its `variant:change` event is dispatched WITHOUT
  // bubbles, so reading the DOM at submit time is the reliable route.
  function selectedVariantId(scope) {
    var node = (scope || document).querySelector('[data-selected-variant]');
    if (!node) node = document.querySelector('[data-selected-variant]');
    if (!node || !node.textContent.trim()) return null;
    try {
      var v = JSON.parse(node.textContent);
      return v && v.id ? String(v.id) : null;
    } catch (e) {
      return null;
    }
  }

  function subscribe(company, email, variantId) {
    return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email: email } } }
          },
          relationships: {
            // Klaviyo's Shopify catalog keys variants as $shopify:::$default:::<id>
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      })
    });
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (resolve) { setTimeout(resolve, ms); })
    ]);
  }

  function wire(wrapper) {
    var form = wrapper.querySelector('form');
    if (!form || form.dataset.ohmBisWired === '1') return;
    form.dataset.ohmBisWired = '1';

    form.addEventListener('submit', function (event) {
      // form.submit() below does not re-fire this listener, but guard anyway.
      if (form.dataset.ohmBisDone === '1') return;

      var company = companyId();
      var emailField = form.querySelector('input[type="email"]');
      var email = emailField && emailField.value ? emailField.value.trim() : '';
      var variantId = selectedVariantId(wrapper.closest('product-info') || document);

      // Nothing to send, or nowhere to send it: let the contact form behave
      // exactly as it does today rather than blocking the shopper.
      if (!company || !email || !variantId) return;

      event.preventDefault();

      withTimeout(
        subscribe(company, email, variantId).catch(function () { /* non-blocking */ }),
        TIMEOUT_MS
      ).then(function () {
        form.dataset.ohmBisDone = '1';
        form.submit();
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-ohm-klaviyo-bis]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Concept re-renders product info on variant change and in quick-view modals,
  // so newly inserted forms need wiring too.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) { init(); return; }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
